import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Nav from "../../components/Nav";
import "./nftgen.scss";

const ASSET_ROOT = "/nftass";

const TRAIT_FILES = {
  background: [
    "antartica.png",
    "bank-vault.png",
    "beach.png",
    "casino.png",
    "cherry-blossoms.png",
    "cyberpunk.png",
    "desert.png",
    "launchpad.png",
    "magic-forest.png",
    "moon-base.png",
    "ocean-floor.png",
    "oval-office.png",
    "penthouse.png",
    "pirate-ship.png",
    "record-store.png",
    "woodstock.png",
  ],
  backAccessories: [
    "fire-aura.png",
    "water-aura.png",
    "moneyaura.png",
    "casino-aura.png",
    "fish-tail.png",
    "fishing-rod.png",
    "mammal-tale.png",
    "trident.png",
    "wing-ange.png",
    "pirate-flag.png",
    "devilwings.png",
    "guitar.png",
    "harpoon.png",
    "paddles.png",
    "sea-flag.png",
    "swords.png",
  ],
  base: ["bob-base.png"],
  body: [
    "beach-shirt.png",
    "greek-cloth.png",
    "grey-shirt.png",
    "orange-ovaralls.png",
    "purple-hoodie.png",
    "red-jacket.png",
    "red-suit.png",
    "suit-black.png",
    "astronaut.png",
    "captain-outfit.png",
    "fish-cult-robe.png",
    "fisherman.png",
    "navy-outfit.png",
    "pirate-outfit.png",
    "prisoner.png",
    "royal.png",
  ],
  eyes: [
    "eye-glasses.png",
    "eye-sunglasses.png",
    "eyes-angry.png",
    "eyes-googly.png",
    "eyes-high.png",
    "captain-monocle.png",
    "dollar-signs.png",
    "fiery-eyes.png",
    "kawaii-eyes.png",
    "ocean-hippie.png",
    "pirate-eyepatch.png",
    "pit-viper-glasses.png",
    "shark-eyes.png",
    "vigilante.png",
    "emo.png",
    "tattoo.png",
  ],
  headwear: [
    "black-durag.png",
    "devil-horns.png",
    "emo-hair.png",
    "halo.png",
    "laurel-leaf.png",
    "MBGA.png",
    "bandana.png",
    "jelly-fish-hat.png",
    "sailor-hat.png",
    "sombrero.png",
    "straw-hat.png",
    "brain.png",
    "chog.png",
    "propeller-hat.png",
    "psy-duck-hair.png",
    "trump-hair.png",
  ],
  mouth: [
    "moth-angry.png",
    "moth-smile.png",
    "moth-toung-out.png",
    "mouth-normal.png",
    "mouth-snark.png",
    "mouth-teeth.png",
    "mouth-with-joint.png",
    "blushing.png",
    "buck-teeth.png",
    "gold-tooth.png",
    "lolipop.png",
    "treasure-map.png",
    "bubble-gum.png",
    "retards-mouth.png",
    "sailor-pipe.png",
    "tongue.png",
  ],
};

const FOLDER_MAP = {
  background: "background",
  backAccessories: "back-accessories",
  base: "Base",
  body: "body",
  mouth: "mouth",
  eyes: "eyes",
  headwear: "Headwear",
};

const DRAW_ORDER = [
  "background",
  "backAccessories",
  "base",
  "body",
  "eyes",
  "mouth",
  "headwear",
];
const TRAIT_SELECT_ORDER = DRAW_ORDER.filter((layer) => layer !== "base");

const toTitle = (name) =>
  name
    .replace(/\.[^/.]+$/, "")
    .replace(/-/g, " ")
    .replace(/\b(moth)\b/gi, "mouth")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildSrc = (layer, file) =>
  `${ASSET_ROOT}/${encodeURIComponent(FOLDER_MAP[layer])}/${encodeURIComponent(file)}`;

const loadImageSingle = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });

const loadImage = async (src) => loadImageSingle(src);

export default function NftGenPage() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [renderCount, setRenderCount] = useState(0);
  const [selectedTraits, setSelectedTraits] = useState(null);
  const [manualSelection, setManualSelection] = useState({});
  const canvasRef = useRef(null);
  const loadedRef = useRef({});

  const traitCatalog = useMemo(() => {
    const catalog = {};
    for (const layer of DRAW_ORDER) {
      catalog[layer] = TRAIT_FILES[layer].map((file) => ({
        file,
        src: buildSrc(layer, file),
        label: toTitle(file),
      }));
    }
    return catalog;
  }, []);

  const drawTraits = useCallback((traits) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const bgImg = traits.background?.img || traits.base?.img;
    const width = bgImg?.naturalWidth || 1000;
    const height = bgImg?.naturalHeight || 1000;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);

    for (const layer of DRAW_ORDER) {
      const part = traits[layer];
      if (!part?.img) continue;
      ctx.drawImage(part.img, 0, 0, width, height);
    }
  }, []);

  const generateRandom = useCallback(() => {
    if (!ready) return;
    const next = {};
    const nextSelection = {};
    for (const layer of DRAW_ORDER) {
      const options = loadedRef.current[layer] || [];
      if (!options.length) continue;
      const idx = Math.floor(Math.random() * options.length);
      next[layer] = options[idx];
      nextSelection[layer] = options[idx].file;
    }
    drawTraits(next);
    setSelectedTraits(next);
    setManualSelection(nextSelection);
    setRenderCount((v) => v + 1);
  }, [drawTraits, ready]);

  const applyManualSelection = useCallback((nextSelection) => {
    if (!ready) return;
    const nextTraits = {};
    for (const layer of DRAW_ORDER) {
      const options = loadedRef.current[layer] || [];
      if (!options.length) continue;
      const chosenFile = nextSelection[layer];
      const selected = options.find((opt) => opt.file === chosenFile) || options[0];
      if (!selected) continue;
      nextTraits[layer] = selected;
    }
    drawTraits(nextTraits);
    setSelectedTraits(nextTraits);
    setRenderCount((v) => v + 1);
  }, [drawTraits, ready]);

  const onManualTraitChange = useCallback((layer, file) => {
    setManualSelection((prev) => {
      const next = { ...prev, [layer]: file };
      applyManualSelection(next);
      return next;
    });
  }, [applyManualSelection]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setReady(false);
        setError("");
        const loaded = {};
        for (const layer of DRAW_ORDER) {
          const entries = traitCatalog[layer];
          loaded[layer] = await Promise.all(
            entries.map(async (entry) => {
              const img = await loadImage(entry.src);
              return { ...entry, img };
            })
          );
        }
        if (cancelled) return;
        loadedRef.current = loaded;
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(String(e?.message || e || "Failed to load NFT assets."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [traitCatalog]);

  useEffect(() => {
    if (!ready) return;
    generateRandom();
  }, [ready, generateRandom]);

  return (
    <div id="nftgen-page">
      <Nav useSmoothScroll={false} />

      <main className="nftgen-main">
        <section className="nftgen-card">
          <header className="nftgen-header">
            <h1>NFT Generator</h1>
            <p>Click once to instantly generate a random BOB NFT from current art layers.</p>
          </header>

          <div className="nftgen-stage">
            <div className="nftgen-canvas-wrap" aria-live="polite">
              {!ready && !error && <div className="nftgen-loading">Loading assets...</div>}
              {error && <div className="nftgen-error">{error}</div>}
              <canvas ref={canvasRef} className={ready ? "ready" : ""} />
            </div>

            <div className="nftgen-controls">
              <button
                type="button"
                className="nftgen-generate-btn"
                onClick={generateRandom}
                disabled={!ready}
              >
                Generate Random NFT
              </button>
              <div className="nftgen-meta">Generated: {renderCount}</div>
            </div>
          </div>

          <div className="nftgen-traits">
            <h2>Current Traits</h2>
            <div className="nftgen-traits-grid">
              {TRAIT_SELECT_ORDER.map((layer) => {
                const options = traitCatalog[layer] || [];
                return (
                  <label key={layer}>
                    <span>{toTitle(layer)}</span>
                    <select
                      value={manualSelection[layer] || ""}
                      onChange={(e) => onManualTraitChange(layer, e.target.value)}
                      disabled={!ready || !options.length}
                    >
                      {!manualSelection[layer] && <option value="">Select {toTitle(layer)}</option>}
                      {options.map((opt) => (
                        <option key={`${layer}-${opt.file}`} value={opt.file}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
